/**
 * Species Catalog Admin Page
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-299
 *
 * Full CRUD management interface for species_catalog table
 * - List view with sortable columns
 * - Search and filters
 * - Pagination (50 items per page)
 * - Status toggle (activate/deactivate)
 * - Edit modal for full species management
 * - Delete with confirmation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Fish, Loader } from 'lucide-react';
import { speciesCatalogAdminApi } from '../../services/catalogAdminApi';
import SpeciesCatalogTable from '../../components/admin/SpeciesCatalogTable';
import SearchBar from '../../components/admin/SearchBar';
import FilterDropdown from '../../components/admin/FilterDropdown';
import Pagination from '../../components/admin/Pagination';
import SpeciesEditModal from '../../components/admin/SpeciesEditModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function SpeciesCatalogAdmin() {
  const navigate = useNavigate();

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Data state
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    speciesType: 'all',
    category: 'all',
    stateCode: 'all',
    isActive: true
  });

  // Sorting & Pagination
  const [sortField, setSortField] = useState('common_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingSpecies, setDeletingSpecies] = useState(null);

  /**
   * Load species from API
   */
  const loadSpecies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        search: searchTerm || undefined,
        species_type: filters.speciesType !== 'all' ? filters.speciesType : undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        state_code: filters.stateCode !== 'all' ? filters.stateCode : undefined,
        is_active: filters.isActive,
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortOrder
      };

      const response = await speciesCatalogAdminApi.listSpecies(params);

      setSpecies(response.data);
      setTotalCount(response.meta.totalCount);
      setTotalPages(response.meta.pages);
    } catch (err) {
      console.error('Failed to load species:', err);
      setError(err.message || 'Failed to load species catalog');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, sortField, sortOrder, currentPage]);

  /**
   * Load species when filters change
   */
  useEffect(() => {
    loadSpecies();
  }, [loadSpecies]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setCurrentPage(1); // Reset to first page
  };

  /**
   * Handle sort changes
   */
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle order if same field
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to asc
      setSortField(field);
      setSortOrder('asc');
    }
  };

  /**
   * Handle create new species
   */
  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingSpecies({
      // Default values for new species
      species_type: 'fish',
      is_active: true
    });
  };

  /**
   * Handle edit species
   */
  const handleEdit = (speciesItem) => {
    setIsCreating(false);
    setEditingSpecies(speciesItem);
  };

  /**
   * Handle save (create or update)
   */
  const handleSave = async (speciesData) => {
    try {
      if (isCreating) {
        await speciesCatalogAdminApi.createSpecies(speciesData);
      } else {
        await speciesCatalogAdminApi.updateSpecies(editingSpecies.id, speciesData);
      }

      // Close modal and reload
      setEditingSpecies(null);
      setIsCreating(false);
      await loadSpecies();
    } catch (err) {
      console.error('Failed to save species:', err);
      throw err; // Re-throw to show error in modal
    }
  };

  /**
   * Handle delete species (soft delete)
   */
  const handleDelete = (speciesItem) => {
    setDeletingSpecies(speciesItem);
  };

  /**
   * Confirm delete
   */
  const confirmDelete = async () => {
    if (!deletingSpecies) {return;}

    try {
      await speciesCatalogAdminApi.deleteSpecies(deletingSpecies.id);
      setDeletingSpecies(null);
      await loadSpecies();
    } catch (err) {
      console.error('Failed to delete species:', err);
      setError(err.message || 'Failed to delete species');
    }
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActive = async (speciesId, currentStatus) => {
    try {
      // If currently active, deactivate (soft delete)
      // If currently inactive, activate
      if (currentStatus) {
        await speciesCatalogAdminApi.deleteSpecies(speciesId);
      } else {
        // Activate by updating is_active to true
        await speciesCatalogAdminApi.updateSpecies(speciesId, { is_active: true });
      }

      await loadSpecies();
    } catch (err) {
      console.error('Failed to toggle species status:', err);
      setError(err.message || 'Failed to update species status');
    }
  };

  /**
   * Handle view audit history
   */
  const handleViewAudit = (speciesItem) => {
    // TODO: Open audit log modal
    console.log('View audit for:', speciesItem);
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="species"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Species Catalog
          </h1>
          <button
            onClick={handleCreateNew}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-4)',
              border: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-white)',
              backgroundColor: 'var(--color-primary)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
              gap: 'var(--space-2)'
            }}
          >
            <Plus style={{ width: '16px', height: '16px' }} />
            Add Species
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Search & Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <SearchBar
                onSearch={setSearchTerm}
                placeholder="Search species by name..."
              />
            </div>

            {/* Species Type Filter */}
            <FilterDropdown
              label="Species Type"
              value={filters.speciesType}
              onChange={(value) => handleFilterChange('speciesType', value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'fish', label: 'Fish' },
                { value: 'game', label: 'Game' },
                { value: 'wildlife', label: 'Wildlife' }
              ]}
            />

            {/* Category Filter */}
            <FilterDropdown
              label="Category"
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
              options={[
                { value: 'all', label: 'All Categories' },
                // Fish categories
                { value: 'Coldwater', label: 'Coldwater' },
                { value: 'Warmwater', label: 'Warmwater' },
                { value: 'Saltwater', label: 'Saltwater' },
                { value: 'Anadromous', label: 'Anadromous' },
                // Game categories
                { value: 'Big Game', label: 'Big Game' },
                { value: 'Small Game', label: 'Small Game' },
                { value: 'Upland Birds', label: 'Upland Birds' },
                { value: 'Waterfowl', label: 'Waterfowl' },
                // Wildlife categories
                { value: 'Birds', label: 'Birds' },
                { value: 'Mammals', label: 'Mammals' },
                { value: 'Reptiles', label: 'Reptiles' },
                { value: 'Amphibians', label: 'Amphibians' }
              ]}
            />

            {/* Active Status Filter */}
            <FilterDropdown
              label="Status"
              value={filters.isActive}
              onChange={(value) => handleFilterChange('isActive', value === 'true')}
              options={[
                { value: true, label: 'Active Only' },
                { value: false, label: 'Inactive Only' },
                { value: 'all', label: 'All Statuses' }
              ]}
            />
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            {loading ? (
              <span>Loading...</span>
            ) : (
              <span>
                Showing {species.length} of {totalCount} species
              </span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-4 text-gray-600">Loading species catalog...</p>
          </div>
        )}

        {/* Species Table */}
        {!loading && species.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SpeciesCatalogTable
              species={species}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              onViewAudit={handleViewAudit}
            />

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && species.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Fish className="w-16 h-16 mx-auto text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No species found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchTerm || filters.speciesType !== 'all' || filters.category !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first species'}
            </p>
            {!searchTerm && filters.speciesType === 'all' && (
              <button
                onClick={handleCreateNew}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Species
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingSpecies && (
        <SpeciesEditModal
          species={editingSpecies}
          isCreating={isCreating}
          onSave={handleSave}
          onClose={() => {
            setEditingSpecies(null);
            setIsCreating(false);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingSpecies && (
        <ConfirmDialog
          title="Deactivate Species"
          message={`Are you sure you want to deactivate "${deletingSpecies.common_name}"? This will mark it as inactive and hide it from guides.`}
          confirmLabel="Deactivate"
          confirmClass="bg-red-600 hover:bg-red-700"
          onConfirm={confirmDelete}
          onCancel={() => setDeletingSpecies(null)}
        />
      )}
    </DashboardLayout>
  );
}
